import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createClipFromLongVideoSkill: Skill = {
    name: SKILL_NAMES.createClipFromLongVideo,
    description:
        'One short, standalone clip cut from a long uploaded recording (a podcast, webinar, interview, talk, livestream, vlog or call): find the strongest moments, let the user pick one, and build it with captions and framing that suit where it will be posted. Use for clips, highlights, teasers, promos, shorts, reels and TikToks taken from a longer video, for requests to find, list or rank its best or most viral moments ("the top 10 clips"), and for picking the part of a recording too long to use whole to put into the video being edited.',
    kind: 'video',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Create a Clip from a Long Video
For turning a long recording into **one** short clip that works on its own for
someone who has never seen the full video. The work splits in two: finding the
moment (most of the value, and the user decides), then making that moment look
native to where it will be posted: framed for the aspect ratio, captioned, and
with the speakers introduced.

Load \`${SKILL_NAMES.talkingHeadVideoBasics}\` before building; it owns how a
recording becomes a scene group. Here the clip is one scene group cut into a
sequence of **scenes**, and the scenes are how you control the picture: each
one has its own crop of the speaker. Most scenes show nothing but the speaker;
text is the exception. This recipe overrides that skill in three places: don't
ask whether to keep it clean, captions are always on (below), and scenes may
cover the whole clip.

**The user always picks the clip, from cards.** Whatever the request says,
proposing clips leads to a \`clip_select\` question (step 2), never to a prose
list and never to a clip you chose yourself:
- "Find the top 10 clips" or "what are the most viral moments" is asking to see
  candidates. The clip question is how you show them, and the picked one is
  then built. A prose list instead is a defect.
- "Give me some clips and make a video" still asks: wanting a video doesn't
  answer which moment goes in it.
- Skip the question only when the user has already named the exact moment
  themselves (its words or its timestamps). Then propose just that clip and
  build it.

**Two kinds of request.** Usually the clip is a new video of its own. Sometimes
it goes **into the video being edited**: the user dropped a recording too long
to use whole into their video, or asked for part of a recording in it. Then
the request says where the clip goes, and everything already in the video
stays. The steps below say where the two differ.

**The build runs in this order, and each step depends on the one before:**
set up and cut the clip, plan the overlays, reframe, then fill in the text,
b-roll and captions. Never add text, b-roll or captions before the framing:
everything placed later is placed around the faces the framing puts on screen.

## 1. Find the moments
- **Call \`${TOOL_NAMES.analyzeVideo}\` first**, before picking anything. Some of
  the best moments are visual (a reaction, something happening on camera) and
  the transcript alone won't show them. Use the panel timestamps to find them,
  then anchor each clip on the speech around that moment.
- **Propose about 10 clips with \`${TOOL_NAMES.proposeClips}\`**, best first, using
  everything you know: the user's request, who the audience is, what the video
  is for. If the user asked for a number, propose that many. **Always call
  it**, never just list clips in prose: it checks every clip against the
  recording and returns its exact times and length, which the user chooses
  from and you build from.
- **What makes a clip:**
  - It stands alone. No missing context, no "as I said earlier".
  - It hooks in the first few seconds: a surprising line, a bold claim, a
    question, a funny or striking moment.
  - It pays off by the end: the point lands, the story resolves. Never cut
    off mid-thought.
  - It starts and ends on clean sentence boundaries, ideally on words without
    a stutter or a repeat right at the edge.
  - **30-90 seconds**, unless the user asked for something else. That is an
    expectation, not a cap: shorter when it's punchy, longer only if it holds.
    For a clip going into the video being edited, let that video decide:
    propose parts that suit what is around them, which may be a few minutes,
    and keep each under 10 minutes.
  - One continuous section of the recording. Don't stitch segments together.
  - Spread across the whole recording, not bunched at the start.
- A teaser or promo for the full video is still one clip. Pick the moment that
  makes someone want the rest, and end on an open question rather than a
  resolution.

## 2. Let the user choose (one questionnaire)
In the **same response** as the \`${TOOL_NAMES.askUserQuestion}\` call, write the
ranked list: each clip's title, the length and position
\`${TOOL_NAMES.proposeClips}\` returned, and one line on why it works. The list is
context for the questionnaire, not your reply: **never end a turn by asking the
user something in prose.** Every question in this recipe goes through
\`${TOOL_NAMES.askUserQuestion}\`.

Following \`${SKILL_NAMES.gatherRequirements}\`, ask everything still open in **one**
call:
- **Which clip.** A \`clip_select\` question with \`pick_one: true\`, so each option
  is a card that plays the clip. Offer every clip you proposed, best first, up
  to 10, each labelled with its title and length (e.g. "The cold open, 0:48"),
  with the same \`start_quote\`
  and \`end_quote\` its \`${TOOL_NAMES.proposeClips}\` segment used. The automatic
  "Other" row is how the user corrects you ("no, the part where they argue
  about pricing"); treat that as a new brief, propose again around it, and ask
  once more the same way.
- **The template**, which the clip's captions, lower thirds and colors all come
  from. It must be settled before the video is created: use the one the request,
  a blueprint or the account's own custom template already implies (as
  \`${SKILL_NAMES.gatherRequirements}\` describes), and otherwise ask here with
  \`type: "template"\`, suggesting a few in \`template_ids\` that support the
  aspect ratio. **Never pick one silently**: the caption question later only
  offers the chosen template's caption styles. A clip going into the video
  being edited keeps that video's template.
- **Where it will be posted**, as \`aspect_ratio\`, unless the request already
  names a platform or ratio, or the clip goes into the video being edited (it
  keeps that video's aspect ratio). Vertical (9:16) is the usual answer for shorts,
  reels and TikTok. Build for one platform: if the user names several, build
  the first and tell them the editor can reframe and export it for the others.
- **Removing ums and pauses.** A \`radio\` question, with the choice that suits
  the clip listed first: a fast, funny or high-energy clip wants both removed; a
  reflective or emotional one usually keeps its pauses and loses only the
  fillers. Say why in the question's \`description\`.
- **The speakers' names and titles**, for lower thirds, when the recording
  doesn't already say who they are. Never invent a name or a title.

Skip any other question the request already answers; the clip question is
skipped only as described at the top. Don't ask about voiceover, script or
music; the recording is the narration.

## 3. Set up the video and cut the clip
- **A clip going into the video being edited:** don't create a video and don't
  rebuild it. Read it with \`${TOOL_NAMES.getVideoState}\` and **keep every scene
  group already in it**. Cut the clip in where the request says, with
  \`after_scene_group_id\` (or at the very start). When it says "in place of" a
  scene group, add the clip right after that group, then delete that group
  only. If the video already has captions, use the same caption design rather
  than asking.
- **A new clip video:** \`${TOOL_NAMES.createBlankVideo}\` with the settled
  template at the chosen aspect ratio. If it says a video already exists, don't
  rebuild: read it with \`${TOOL_NAMES.getVideoState}\`, add your clip's scene
  group and delete any scene group you didn't build.
- **As soon as the video exists, ask the caption style on its own**, however the
  video came to exist, with an \`${TOOL_NAMES.askUserQuestion}\` of
  \`type: "caption"\`. Skip it only when the user already named a style, or the
  recording has burned-in captions (check one frame with \`${TOOL_NAMES.imageAsk}\`),
  in which case captions stay off. **Picking a caption style without asking is
  a defect** in this format, and so is leaving captions off because nobody
  asked. If the user dismisses the picker, choose a bold, high-contrast design
  yourself.
- **Cut the clip** with \`${TOOL_NAMES.addTalkingHeadSceneGroup}\` and the chosen
  clip's **\`start_quote\` and \`end_quote\`**: the same quotes its
  \`${TOOL_NAMES.proposeClips}\` segment used (the \`clip_select\` answer echoes them
  back), which are already known to resolve. The scene group then holds only
  the clip; never add the whole recording and trim it down. Don't pass the
  clip's full \`words\`: copying a long passage word for word goes wrong at
  repeats and stutters.
- If the quotes are rejected, nothing is added; fix the quote the error names
  and call it again. **The clip is exactly one scene group.** Never assemble it
  from several smaller ones, and never shorten the clip to get a match: the
  user chose that clip. If it still won't resolve, tell the user what happened.
- Then \`${TOOL_NAMES.cleanUpRecording}\` with what the user chose, if anything, and
  apply the caption style with \`${TOOL_NAMES.setCaptions}\`: just its \`design_id\`,
  never the font, size or colors on top. The picked design is what the user
  chose; restyling it is a defect. Its position comes after the framing.
- **Never \`${TOOL_NAMES.splitSceneGroup}\`** to change the crop, add b-roll or
  introduce a speaker. Those are scenes (\`${TOOL_NAMES.addVoiceoverScene}\`) inside
  the one group. Splitting fragments captions, cleanup and timing.
- Don't call \`${TOOL_NAMES.listAvailableMedia}\` once the clip is cut; it reprints
  the whole recording's transcript.
- **If the recording has no picture** (an audio-only podcast), there is nothing
  to frame. Build the group with \`${TOOL_NAMES.addRecordedVoiceoverSceneGroup}\` (it
  takes the clip's full \`words\`, not quotes), give it a waveform design with
  \`${TOOL_NAMES.findWaveformSceneDesign}\`, and skip the reframing step.
  A recorded voiceover going into the video being edited is narration, not a
  podcast clip: after cutting it in, load
  \`${SKILL_NAMES.recordedVoiceoverVideoBasics}\` and build its supporting scenes
  the way the video's other narration is built, instead of a waveform.

## 4. Plan the overlays
Before anything visual, decide what will sit on top of the speaker, and
**write the plan in your message**, each item with the exact words it covers.
Add nothing yet: the reframing step builds its scenes around this plan.
- **Lower thirds: yes, when you know who is speaking.** One per speaker, over
  their first words in the clip, using the names from step 2, for a sentence
  or so, never stretched over the speaker's whole turn. Never invent a name or
  a title.
- **A callout: conservative** unless the user asked for on-screen text. At most
  one, over the key stat or claim, for about one sentence. The captions
  already carry the words, so a callout repeating them is clutter.
- **B-roll: sparing, but not afraid of it.** Plan a cutaway only where the
  speaker names something concrete and visual, for a sentence or so. A vaguely
  related clip is worse than staying on the speaker.
- **Captions** are always on; they go wherever the framing leaves room.

## 5. Reframe for the format
Load \`${SKILL_NAMES.reframeClip}\` and follow it now, before any text, b-roll or
caption position. It owns everything about the picture: the shot table, which
scenes to add, the speaker designs, framing each scene, and checking the
crops. Give it the plan from step 4: the planned lower thirds and callout
become speaker scenes on empty lower-third and callout designs, and the
planned b-roll words stay out of the speaker scenes.

## 6. Fill in the overlays
Once every scene is framed:
- **Lower thirds and the callout:** set the text on the scenes the reframing
  built for them, with \`${TOOL_NAMES.setText}\`. Make sure each name sits over the
  person actually on screen in that scene.
- **B-roll:** load \`${SKILL_NAMES.findStockMedia}\` first, then add each planned
  cutaway as a scene on the cutaway design over its words, with
  \`${TOOL_NAMES.setMedia}\` for the footage.
- **Captions: on for the whole clip.** Big, legible captions are this format's
  main visual. Look at where the faces ended up and use \`${TOOL_NAMES.setCaptions}\`
  \`position\` (and only \`position\`) to put them in the space that leaves, clear
  of faces and lower thirds. In a stack, that space is the seam, the line across
  the middle of the frame where the two slots meet. Captions overlapping a
  lower third or a face are a defect. Turn them off only on scenes that carry
  an animated-captions design, so two caption treatments never show at once.
- **When a review finds text over a face, move the text or the captions.**
  Don't delete the lower third or the callout to make the defect go away, and
  don't move a crop off a face.

## Styling
Colors come from the template or the user's brand kit
(\`${SKILL_NAMES.applyBrandKit}\` when they have one). Don't hand-pick overrides.

## When the user corrects you
Do what they asked first, then check the result. If they say a scene shows the
wrong thing or tell you how to fix a crop, apply it rather than arguing from a
tool's description of the frame: they are looking at the actual video. "Make it
match that scene" means copy that scene's values exactly.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **The clip is the one the user chose**, starts and ends on whole sentences,
  and is close to the length you proposed (the cleanup shortens it a little).
- **There is exactly one scene group**, and nothing was split off it.
- **The framing passed \`${SKILL_NAMES.reframeClip}\`'s checks**, and nothing added
  since moved a crop.
- **Captions are on and clear of faces and lower thirds**: run
  \`${TOOL_NAMES.reviewSceneImages}\` on a sample of scenes.
- **Text stays the exception**: one lower third per speaker, at most one
  callout.
- **Lower thirds name only people the user or the recording named.**
- **Your closing summary describes what is actually on screen.**`;
    },
};
